import { Card, Icon } from '@iwoca/orion';
import styles from '../NewOutstandingView/NewOutstandingView.module.css';
import overdueStyles from './NewOverdueView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';

import InternationalisedName from '../dashboardComponents/InternationalisedName';
import { Rate } from '../../ui/rate';
import ProductType from '../../ui/productType/ProductType';

import { Tab } from '@headlessui/react';
import { FAQs } from '../NewOutstandingView/FAQs/FAQs';

import { RootState } from '@iwProvider/configureStore';
import * as selectors from '../accountsSelector';
import { TProductType } from '../../dashboard/Dashboard.types';
import { connect } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import loanCostMessages from '../dashboardComponents/loanCost/LoanCost.messages';
import { FormattedMessage } from 'react-intl';
import { getMaxMonthlyInterestRate } from '../NewOutstandingView/NewOutstandingView.helpers';
import OverviewPanel from '../NewOutstandingView/OverviewPanel/OverviewPanel';
import { RepaymentsPanel } from '../NewOutstandingView/RepaymentsPanel/RepaymentsPanel';
import { OverduePayments } from '../dashboardComponents/overduePayments/OverduePayments';
import { Dispatch } from 'redux';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { EARLY_REPAYMENT_ACCOUNT_ID } from '../../../myAccount/earlyRepayment/EarlyRepayment.actions';
import { OverdueAlert } from './OverdueAlert';

type OwnProps = {
    productType: TProductType;
    accountId: string;
};

const mapStateToProps = (state: RootState, { productType }: OwnProps) => {
    return {
        loanDuration: selectors.getLoanDurationByMonth(state, productType),
        creditLimit: selectors.getMaxAmountCreditAvailable(state, productType),
        overduePaymentAmount: selectors.getNextRepaymentAmount(
            state,
            productType
        )
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToEarlyRepayment: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/repayment/' }
        }),
    addAccountIdToEarlyRepayment: (accountId: string) =>
        dispatch({
            type: EARLY_REPAYMENT_ACCOUNT_ID,
            accountId
        })
});
type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const NewOverdueView = ({
    productType,
    accountId,
    loanDuration,
    overduePaymentAmount,
    goToEarlyRepayment,
    addAccountIdToEarlyRepayment
}: OwnProps & PropsFromRedux) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            // @ts-expect-error
            product_type: productType,
            dashboard_state: 'overdue'
        });
    }, [accountId, productType]);

    const handleOverdueRepaymentClick = useCallback(() => {
        addAccountIdToEarlyRepayment(accountId);
        goToEarlyRepayment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles.container}>
            <OverdueAlert overdueAmount={overduePaymentAmount} />

            <Card className={styles.cardContainer}>
                <LoanInformationHeader accountId={accountId} />

                <Tab.Group
                    selectedIndex={selectedIndex}
                    onChange={setSelectedIndex}
                >
                    <Tab.List className={styles.tabsList}>
                        <Tab className={styles.tab}>
                            <Icon icon="houseFilled" className={styles.icon} />{' '}
                            Overview
                        </Tab>

                        <Tab className={styles.tab}>
                            <Icon icon="sync" className={styles.icon} />{' '}
                            Repayments
                        </Tab>
                    </Tab.List>

                    <div className={styles.tabPanel}>
                        <Tab.Panels>
                            <Tab.Panel>
                                <OverviewPanel accountId={accountId} />

                                <div className={overdueStyles.actions}>
                                    <OverduePayments
                                        overduePaymentAmount={
                                            overduePaymentAmount
                                        }
                                        onClick={handleOverdueRepaymentClick}
                                        isBottomAction
                                    />
                                </div>
                            </Tab.Panel>

                            <Tab.Panel>
                                <RepaymentsPanel
                                    isOverdue={true}
                                    accountId={accountId}
                                />
                            </Tab.Panel>
                        </Tab.Panels>

                        <FAQs loanDuration={loanDuration} isOverdue />
                    </div>
                </Tab.Group>
            </Card>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(NewOverdueView);

const LoanInformationHeader = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    // TODO: Improve loading skeleton
    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const productType = account.product_type;

    const { amount, unit } = account.duration!;
    const durationInMonths =
        unit === 'days' ? selectors.getMonthsfromDays(amount) : amount;
    const isLongerLoan = durationInMonths > 24;

    const interestRate = getMaxMonthlyInterestRate(account);

    return (
        <>
            <p className={styles.greeting}>
                Hello, <InternationalisedName />
            </p>
            <p className={styles.loanInformation}>
                You're looking at your{' '}
                <ProductType
                    productType={productType!}
                    isDynamic={isLongerLoan}
                />
                <FormattedMessage
                    {...loanCostMessages.youllPay}
                    values={{
                        approvalInterestRate: <Rate value={interestRate!} />
                    }}
                />
            </p>
        </>
    );
};
